// What desyr knows about the company's chat server (03-startup-session.md): its versions answer and
// what it turns on, the call service from the discovery file, and the details asked for in the
// background once the app is open. Whatever couldn't be read is simply missing.
import { serverOf, type Session } from './session';

export interface Versions {
  versions: string[];
  unstable_features?: Record<string, boolean>;
}

export interface Features {
  /** Media needs the access token (v1.11 or the stable MSC3916 flag). */
  authenticatedMedia: boolean;
  /** Channels can be reported (v1.13 or MSC4151). */
  reportRooms: boolean;
  /** "Channels in common" (MSC2666): the stable or the unstable endpoint, or none. */
  mutualRooms: 'stable' | 'unstable' | false;
  /** Messages sent later by the server (MSC4140; 29's Send later). */
  delayedEvents: boolean;
}

export const server: {
  features: Features;
  /** The MSC4143 call-service list from the discovery file (22-calls.md). */
  callServices?: unknown[];
  capabilities?: unknown;
  mediaConfig?: { 'm.upload.size'?: number };
  authMetadata?: unknown;
} = { features: { authenticatedMedia: false, reportRooms: false, mutualRooms: false, delayedEvents: false } };

const atLeast = (versions: string[], wanted: string) => {
  const [wMajor, wMinor] = wanted.slice(1).split('.').map(Number);
  return versions.some((v) => {
    const [major, minor] = v.replace(/^[rv]/, '').split('.').map(Number);
    return major > wMajor || (major === wMajor && minor >= wMinor);
  });
};

export function featuresOf(answer: Versions): Features {
  const flag = (name: string) => answer.unstable_features?.[name] === true;
  const v = answer.versions.filter((x) => x.startsWith('v'));
  const oldMutual = ['uk.half-shot.msc2666', 'uk.half-shot.msc2666.mutual_rooms', 'uk.half-shot.msc2666.query_mutual_rooms'];
  return {
    authenticatedMedia: atLeast(v, 'v1.11') || flag('org.matrix.msc3916.stable'),
    reportRooms: atLeast(v, 'v1.13') || flag('org.matrix.msc4151'),
    mutualRooms: atLeast(v, 'v1.19') || flag('uk.half-shot.msc2666.query_mutual_rooms.stable') ? 'stable' : oldMutual.some(flag) ? 'unstable' : false,
    delayedEvents: flag('org.matrix.msc4140'),
  };
}

/** GET /_matrix/client/versions; an answer without a versions list is a failure. */
export async function fetchVersions(baseUrl: string): Promise<Versions> {
  const answer = await fetch(`${baseUrl}/_matrix/client/versions`);
  if (!answer.ok) throw Object.assign(new Error('versions'), { status: answer.status });
  const body = await answer.json();
  if (!Array.isArray(body?.versions)) throw new Error('versions: no list');
  return body as Versions;
}

const CALL_SERVICES = 'org.matrix.msc4143.rtc_foci';

async function wellKnown(origin: string): Promise<Record<string, unknown> | undefined> {
  try {
    const answer = await fetch(`${origin}/.well-known/matrix/client`);
    return answer.ok ? await answer.json() : undefined;
  } catch {
    return undefined;
  }
}

/** In the background: the call-service list from the user's server, else from the base URL's. */
export async function discoverCallServices(session: Session): Promise<void> {
  for (const origin of [`https://${serverOf(session.user_id)}`, session.base_url]) {
    const list = (await wellKnown(origin))?.[CALL_SERVICES];
    if (Array.isArray(list)) return void (server.callServices = list);
  }
}
